import { useEffect, useMemo, useRef, useState } from "react";
import "./css/DynamicImageWindow.css";

export interface DynamicImageWindowProps {
    portraitImage: string;
    landscapeImage: string;

    /**
     * Percentage overscan applied to the
     * longest image dimension.
     */
    overscanPercent?: number;

    transitionDurationSeconds?: number;
}

interface Size {
    width: number;
    height: number;
}

interface Position {
    left: number;
    top: number;
}

export function DynamicImageWindow({
    portraitImage,
    landscapeImage,
    overscanPercent = 20,
    transitionDurationSeconds = 12
}: DynamicImageWindowProps) {

    const containerRef = useRef<HTMLDivElement>(null);

    const [viewportSize, setViewportSize] =
        useState<Size | null>(null);

    const [imageNaturalSize, setImageNaturalSize] =
        useState<Size | null>(null);

    const [isPortraitViewport, setIsPortraitViewport] =
        useState(false);

    const [position, setPosition] =
        useState<Position | null>(null);

    useEffect(() => {

        const container = containerRef.current;

        if (!container) {
            return;
        }

        const observer = new ResizeObserver(entries => {

            const rect = entries[0].contentRect;

            setViewportSize({
                width: rect.width,
                height: rect.height
            });

            setIsPortraitViewport(
                rect.height > rect.width
            );
        });

        observer.observe(container);

        return () => observer.disconnect();

    }, []);

    const selectedImage = useMemo(
        () =>
            isPortraitViewport
                ? portraitImage
                : landscapeImage,
        [
            isPortraitViewport,
            portraitImage,
            landscapeImage
        ]
    );

    useEffect(() => {

        setImageNaturalSize(null);

        const image = new Image();

        image.onload = () => {

            setImageNaturalSize({
                width: image.naturalWidth,
                height: image.naturalHeight
            });
        };

        image.src = selectedImage;

    }, [selectedImage]);

    const renderedImageSize = useMemo(() => {

        if (!viewportSize) {
            return null;
        }

        if (!imageNaturalSize) {
            return null;
        }

        const vw = viewportSize.width;
        const vh = viewportSize.height;

        if (vw <= 0 || vh <= 0) {
            return null;
        }

        const iw = imageNaturalSize.width;
        const ih = imageNaturalSize.height;

        const imageAspect = iw / ih;

        const isLandscapeImage = iw >= ih;

        //
        // Scale required to satisfy
        // longest-dimension overscan rule.
        //
        let overscanScale: number;

        if (isLandscapeImage) {

            overscanScale =
                (vw * (1 + overscanPercent / 100)) / iw;

        } else {

            overscanScale =
                (vh * (1 + overscanPercent / 100)) / ih;
        }

        //
        // Scale required for complete
        // viewport coverage.
        //
        const coverScale = Math.max(
            vw / iw,
            vh / ih
        );

        const finalScale = Math.max(
            overscanScale,
            coverScale
        );

        return {
            width: iw * finalScale,
            height: ih * finalScale,
            aspect: imageAspect
        };

    }, [
        viewportSize,
        imageNaturalSize,
        overscanPercent
    ]);

    useEffect(() => {

        if (!renderedImageSize || !viewportSize) {
            return;
        }

        const minLeft =
            viewportSize.width -
            renderedImageSize.width;

        const maxLeft = 0;

        const minTop =
            viewportSize.height -
            renderedImageSize.height;

        const maxTop = 0;

        const createTarget = (): Position => ({

            left:
                minLeft +
                Math.random() *
                (maxLeft - minLeft),

            top:
                minTop +
                Math.random() *
                (maxTop - minTop)
        });

        const startPosition = createTarget();
        const firstDestination = createTarget();

        //
        // Render image at the start position.
        //
        setPosition(startPosition);

        //
        // After the browser has painted,
        // animate immediately to the first
        // destination.
        //
        requestAnimationFrame(() => {

            requestAnimationFrame(() => {

                setPosition(firstDestination);

            });

        });

        const intervalId = window.setInterval(() => {

            setPosition(createTarget());

        }, transitionDurationSeconds * 1000);

        return () => {

            window.clearInterval(intervalId);

        };

    }, [
        renderedImageSize,
        viewportSize,
        transitionDurationSeconds
    ]);

    return (
        <div
            ref={containerRef}
            className="dynamic-image-window"
        >
            {position && <span>`position.top:${position.top},position.left:${position.left}`</span>}
            {
                renderedImageSize &&
                position &&
                <img
                    className="dynamic-image-window__image"
                    src={selectedImage}
                    alt=""
                    draggable={false}
                    style={{
                        width: `${renderedImageSize.width}px`,
                        height: `${renderedImageSize.height}px`,
                        left: `${position.left}px`,
                        top: `${position.top}px`,
                        transitionDuration:
                            `${transitionDurationSeconds}s`
                    }}
                />
            }
        </div>
    );
}
